import { ApiTransport } from "./config/apiTransport";
import {
  EventListDTO,
  EventListResponse,
  GetAllUsersDto,
  GetAllUsersResponse,
  OrganizationFollowingListResponse,
  OrganizationListDTO,
  OrganizationListResponse,
  OrganizationsFollowingListDTO,
  UserDto,
  UserResponse,
} from "@root/utils/dtos";

export class UserService {
  private readonly _apiTransport: ApiTransport;

  constructor(sessionCookie?: string) {
    this._apiTransport = new ApiTransport(sessionCookie);
  }

  getUserByEmail = (email: string): UserResponse =>
    this._apiTransport.get<UserDto>(`/api/v1/users/email/${email}`);

  getUserById = (id: string): UserResponse =>
    this._apiTransport.get<UserDto>(`/api/v1/users/${id}`);

  getOrganizations = (id: string): OrganizationListResponse =>
    this._apiTransport.get<OrganizationListDTO>(
      `/api/v1/users/${id}/organizations`,
    );

  getEvents = (
    id: string,
    organizationId?: string,
    page: number = 0,
  ): EventListResponse => {
    const params: string[] = [];
    if (organizationId) params.push(`organizationId=${organizationId}`);
    params.push(`page=${page}`);

    const qs = params.length > 0 ? `?${params.join("&")}` : "";

    return this._apiTransport.get<EventListDTO>(
      `/api/v1/users/${id}/events${qs}`,
    );
  };

  attendEvent = (userId: string, eventId: string) =>
    this._apiTransport.post<{ isAttended: boolean; isFollowed: boolean }>(
      `/api/v1/users/${userId}/events/${eventId}/attend`,
    );

  followEvent = (userId: string, eventId: string) =>
    this._apiTransport.post(`/api/v1/users/${userId}/events/${eventId}/follow`);

  unfollowEvent = (userId: string, eventId: string) =>
    this._apiTransport.delete(
      `/api/v1/users/${userId}/events/${eventId}/follow`,
    );

  followOrganization = (userId: string, organizationId: string) =>
    this._apiTransport.post(
      `/api/v1/users/${userId}/organizations/${organizationId}/follow`,
    );

  unfollowOrganization = (userId: string, organizationId: string) =>
    this._apiTransport.delete(
      `/api/v1/users/${userId}/organizations/${organizationId}/follow`,
    );

  reportEvent = (userId: string, eventId: string, reason: string) =>
    this._apiTransport.post(
      `/api/v1/users/${userId}/events/${eventId}/report`,
      { reason },
    );

  reportOrganization = (
    userId: string,
    organizationId: string,
    reason: string,
  ) =>
    this._apiTransport.post(
      `/api/v1/users/${userId}/organizations/${organizationId}/report`,
      { reason },
    );

  getFollowedOrganizations = (id: string): OrganizationFollowingListResponse =>
    this._apiTransport.get<OrganizationsFollowingListDTO>(
      `/api/v1/users/${id}/organizations/following`,
    );

  getFollowedEvents = (id: string): EventListResponse =>
    this._apiTransport.get<EventListDTO>(
      `/api/v1/users/${id}/events/following`,
    );

  hideEvent = (userId: string, eventId: string) =>
    this._apiTransport.post(`/api/v1/users/${userId}/events/${eventId}/hide`);

  unhideEvent = (userId: string, eventId: string) =>
    this._apiTransport.post(`/api/v1/users/${userId}/events/${eventId}/unhide`);

  getHiddenEvents = (id: string): EventListResponse =>
    this._apiTransport.get<EventListDTO>(`/api/v1/users/${id}/events/hidden`);

  getAllUsers = (
    page: number = 0,
    pageSize: number = 25,
    searchTerm?: string,
  ): GetAllUsersResponse =>
    this._apiTransport.get<GetAllUsersDto>(
      `/api/v1/users/all?page=${page}${pageSize !== 25 ? `&pageSize=${pageSize}` : ""}${searchTerm ? `&searchTerm=${encodeURIComponent(searchTerm)}` : ""}`,
    );

  deleteUser = (userId: string, isHardDelete: boolean) =>
    this._apiTransport.delete(
      `/api/v1/users/${userId}${isHardDelete ? "?hardDelete=true" : ""}`,
    );
}

export const userService = new UserService();
